import { createContext, ReactNode, useEffect, useReducer } from 'react'

import { JWTContextType, ActionMap, AuthState } from '../types/auth'

import { getUserIdFromToken, isValidToken, setSession, setTemporaryToken } from '../utils/jwt'

import authService, { sendEmailVerificationMail } from '../services/authService'
import userService from '../services/userService'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useQuery from '../hooks/useQuery'
import { UserRole } from '../enums/UserRole'
import { MFAType } from '../enums'
import { CurrentUser, User, Company } from '../types'

const INITIALIZE = 'INITIALIZE'
const SIGN_IN = 'SIGN_IN'
const SIGN_OUT = 'SIGN_OUT'
const SIGN_UP = 'SIGN_UP'

type AuthActionTypes = {
  [INITIALIZE]: {
    isAuthenticated: boolean
    currentUser: User | null
  }
  [SIGN_IN]: {
    currentUser: User
  }
  [SIGN_OUT]: undefined
  [SIGN_UP]: {
    currentUser: User
  }
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  currentUser: null,
  setCurrentUser: () => {
    // Do nothing.
  }
}

const JWTReducer = (
  state: AuthState,
  action: ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>]
) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        currentUser: action.payload.currentUser
      }
    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        currentUser: action.payload.currentUser
      }
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null
      }

    case SIGN_UP:
      return {
        ...state,
        isAuthenticated: true,
        currentUser: action.payload.currentUser
      }

    default:
      return state
  }
}

const AuthContext = createContext<JWTContextType | null>(null)

function AuthProvider({ children }: { children: ReactNode }) {
  /**
   * The state.
   */
  const [state, dispatch] = useReducer(JWTReducer, initialState)

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The query.
   */
  const query = useQuery()

  const { i18n } = useTranslation()

  const handleDefaultLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
  }

  const handleUserNavigation = () => {
    const redirect = query.get('redirect')
    if (redirect) {
      navigate(redirect)
    } else if (state.currentUser?.role === UserRole.MANUFACTURER) {
      navigate('/manufacturing')
    } else {
      navigate('/')
    }
  }

  /**
   * The access token.
   *
   * @returns accessToken
   */
  const accessToken = window.localStorage.getItem('accessToken')

  /**
   * The refresh token.
   *
   * @returns refreshToken
   */
  const refreshToken = window.localStorage.getItem('refreshToken')

  const signIn = async (email: string, password: string, token?: string) => {
    const { accessToken, refreshToken, ...user } = await authService.login({
      email,
      password,
      token
    })

    if (!user) {
      navigate(`auth/500`)
    }

    const isMissingCompany: boolean = checkCompany(user.company, user.role)

    if (isMissingCompany) {
      return navigate('/auth/company-archived')
    }

    if (user) {
      if (user.emailVerifiedAt === null) {
        setTemporaryToken(accessToken)
        await sendEmailVerificationMail()
        navigate(`auth/verify-email`)
      } else if (user.mfaType !== MFAType.NONE) {
        const redirect = query.get('redirect')
        const to = redirect
          ? `auth/mfa/user?redirect=${redirect}&type=${user.mfaType}`
          : `auth/mfa/user?type=${user.mfaType}`

        setTemporaryToken(accessToken)
        navigate(to)
      } else {
        setSession(accessToken, refreshToken)
        dispatch({
          type: SIGN_IN,
          payload: {
            currentUser: user
          }
        })
        window.localStorage.setItem('language', user.language)
        handleDefaultLanguageChange(user.language)
        handleUserNavigation()
      }
    }
  }

  const signOut = async () => {
    setSession(null, null)
    dispatch({ type: SIGN_OUT })
  }

  const completeSignIn = async ({ accessToken, refreshToken, ...user }: CurrentUser) => {
    setSession(accessToken, refreshToken)
    dispatch({
      type: SIGN_IN,
      payload: {
        currentUser: user
      }
    })

    window.localStorage.setItem('language', user.language)
    handleDefaultLanguageChange(user.language)
    handleUserNavigation()
  }

  async function refreshUser() {
    const accessToken = window.localStorage.getItem('accessToken')
    const refreshToken = window.localStorage.getItem('refreshToken')

    if (refreshToken && isValidToken(refreshToken)) {
      setSession(accessToken, refreshToken)

      const userId = getUserIdFromToken(refreshToken)

      if (!userId) {
        throw Error('User ID not found in token')
      }

      const user = await userService.getUser(userId)

      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: true,
          currentUser: user
        }
      })
    } else {
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          currentUser: null
        }
      })
    }
  }

  function checkCompany(company: Company | null | undefined, role: UserRole): boolean {
    if (
      company === null &&
      role !== UserRole.GUEST &&
      role !== UserRole.INSTALLER &&
      role !== UserRole.MANUFACTURER &&
      role !== UserRole.SUPERADMIN
    ) {
      return true
    } else return false
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken')
        const refreshToken = window.localStorage.getItem('refreshToken')

        if (refreshToken && isValidToken(refreshToken)) {
          setSession(accessToken, refreshToken)

          const userId = getUserIdFromToken(refreshToken)

          if (!userId) {
            throw Error('User ID not found in token')
          }

          const user = await userService.getUser(userId)

          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: true,
              currentUser: user
            }
          })
        } else {
          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: false,
              currentUser: null
            }
          })
        }
      } catch (_error) {
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: false,
            currentUser: null
          }
        })
      }
    }

    initialize()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        accessToken,
        refreshToken,
        signIn,
        signOut,
        completeSignIn,
        refreshUser,
        redirect: handleUserNavigation
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
